import { useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { Plus, Trash2 } from 'lucide-react';
import {
  AdminPage,
  Button,
  EmptyState,
  ErrorState,
  Skeleton,
  StatusBadge,
  Table,
  Td,
  Th,
  Tr,
  useConfirm,
} from '@/design/components';
import { usePerm } from '@/access/usePerm';
import { errorMessage } from '@/lib/errors';
import { useAdminCourses, useCourseMutations } from './api';

/** רשימת הלומדות וניהולן (PRD 5.6.1). */
export function CoursesPage() {
  const confirm = useConfirm();
  const canManage = usePerm('learning.manage');
  const courses = useAdminCourses();
  const { createCourse, removeCourse } = useCourseMutations();
  const [title, setTitle] = useState('');
  const [error, setError] = useState<string | null>(null);

  if (!canManage) return <Navigate to="/admin" replace />;

  const run = (action: () => Promise<unknown>) => {
    setError(null);
    void action().catch((caught) => setError(errorMessage(caught)));
  };

  return (
    <AdminPage title="לומדות" subtitle="בניית לומדות וחידונים">
      {error && (
        <p role="alert" className="m-0 text-body-sm text-bad">
          {error}
        </p>
      )}

      <form
        className="flex flex-wrap items-end gap-2"
        onSubmit={(event) => {
          event.preventDefault();
          const name = title.trim();
          if (!name) return;
          run(async () => {
            await createCourse.mutateAsync(name);
            setTitle('');
          });
        }}
      >
        <div className="grid gap-1">
          <label htmlFor="course-title" className="text-body-sm text-muted">
            שם הלומדה
          </label>
          <input
            id="course-title"
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            className="min-h-11 w-64 max-w-full rounded-sm border border-muted bg-surface px-3 text-body text-ink"
          />
        </div>
        <Button variant="primary" type="submit" disabled={createCourse.isPending}>
          <Plus size={20} strokeWidth={1.75} aria-hidden="true" />
          יצירת לומדה
        </Button>
      </form>

      {courses.isPending && <Skeleton className="h-40" />}
      {courses.isError && <ErrorState onRetry={() => void courses.refetch()} />}

      {courses.data?.length === 0 && (
        <EmptyState
          title="אין עדיין לומדות"
          description="אפשר ליצור לומדה ראשונה בשדה שלמעלה."
        />
      )}

      {courses.data && courses.data.length > 0 && (
        <Table>
          <thead>
            <Tr>
              <Th>שם</Th>
              <Th>קטגוריה</Th>
              <Th>שיעורים</Th>
              <Th>חידון</Th>
              <Th>נקודות</Th>
              <Th>מצב</Th>
              <Th>
                <span className="sr-only">פעולות</span>
              </Th>
            </Tr>
          </thead>
          <tbody>
            {courses.data.map((course) => (
              <Tr key={course.id}>
                <Td>
                  <Link to={`/admin/learning/${course.id}`} className="text-ink">
                    {course.title}
                  </Link>
                </Td>
                <Td>{course.course_categories?.name ?? '—'}</Td>
                <Td className="tabular">{course.course_lessons[0]?.count ?? 0}</Td>
                <Td>{course.quizzes.length > 0 ? 'יש' : '—'}</Td>
                <Td className="tabular">{course.points_reward}</Td>
                <Td>
                  {course.is_published ? (
                    <StatusBadge tone="good">מפורסמת</StatusBadge>
                  ) : (
                    <StatusBadge tone="warn">טיוטה</StatusBadge>
                  )}
                </Td>
                <Td>
                  <Button
                    variant="ghost"
                    aria-label={`מחיקת ${course.title}`}
                    onClick={() =>
                      confirm.ask({
                        title: 'מחיקת לומדה',
                        body: (
                          <>
                            <p className="m-0">"{course.title}"</p>
                            <p className="m-0 text-body-sm text-muted">
                              יימחקו גם השיעורים, החידון, וההתקדמות של כל מי שכבר למד. אי אפשר
                              לשחזר את זה.
                            </p>
                          </>
                        ),
                        confirmLabel: 'מחיקת הלומדה וההתקדמות',
                        onConfirm: () => run(() => removeCourse.mutateAsync(course.id)),
                      })
                    }
                  >
                    <Trash2 size={20} strokeWidth={1.75} aria-hidden="true" />
                  </Button>
                </Td>
              </Tr>
            ))}
          </tbody>
        </Table>
      )}
      {confirm.dialog}
    </AdminPage>
  );
}
